import { BaseTool } from './BaseTool';
import { ToolType } from '../types';

export class BucketTool extends BaseTool {
  public id: ToolType = 'bucket';
  public name = 'Paint Bucket Tool';
  public cursor = 'crosshair';

  public onMouseDown(_e: MouseEvent, canvasX: number, canvasY: number): void {
    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible) return;

    const lx = Math.floor(canvasX - layer.x);
    const ly = Math.floor(canvasY - layer.y);

    if (lx < 0 || lx >= layer.width || ly < 0 || ly >= layer.height) {
      return;
    }

    const settings = this.editor.toolSettings;
    const tol = settings.bucketTolerance;
    const contig = settings.bucketContiguous;
    const fillColor = this.parseHexColor(settings.brushColor);

    this.floodFill(layer, lx, ly, fillColor, tol, contig);
    this.editor.recordHistory('Paint Bucket Fill');
    this.editor.render();
  }

  private parseHexColor(hex: string): [number, number, number, number] {
    let c = hex.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map(x => x + x).join('');
    }
    const num = parseInt(c, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255, 255];
  }

  private floodFill(
    layer: any,
    startX: number,
    startY: number,
    fillRgba: [number, number, number, number],
    tolerance: number,
    contiguous: boolean
  ) {
    const width = layer.width;
    const height = layer.height;
    const imgData = layer.ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    const startIdx = (startY * width + startX) * 4;
    const tr = data[startIdx];
    const tg = data[startIdx + 1];
    const tb = data[startIdx + 2];
    const ta = data[startIdx + 3];

    // If target color is already fill color, return
    if (
      tr === fillRgba[0] &&
      tg === fillRgba[1] &&
      tb === fillRgba[2] &&
      ta === fillRgba[3]
    ) {
      return;
    }

    const tolSq = tolerance * tolerance * 3;

    const matches = (idx: number): boolean => {
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      if (ta === 0 && a === 0) return true;
      if (Math.abs(ta - a) > tolerance * 2) return false;

      const dr = r - tr;
      const dg = g - tg;
      const db = b - tb;
      return (dr * dr + dg * dg + db * db) <= tolSq;
    };

    if (!contiguous) {
      for (let i = 0; i < width * height; i++) {
        const idx = i * 4;
        if (matches(idx)) {
          data[idx] = fillRgba[0];
          data[idx + 1] = fillRgba[1];
          data[idx + 2] = fillRgba[2];
          data[idx + 3] = fillRgba[3];
        }
      }
    } else {
      const visited = new Uint8Array(width * height);
      const queue = [startX, startY];
      visited[startY * width + startX] = 1;

      let head = 0;
      while (head < queue.length) {
        const cx = queue[head++];
        const cy = queue[head++];
        const idx = (cy * width + cx) * 4;

        data[idx] = fillRgba[0];
        data[idx + 1] = fillRgba[1];
        data[idx + 2] = fillRgba[2];
        data[idx + 3] = fillRgba[3];

        const neighbors = [
          [cx + 1, cy],
          [cx - 1, cy],
          [cx, cy + 1],
          [cx, cy - 1]
        ];

        for (let i = 0; i < 4; i++) {
          const nx = neighbors[i][0];
          const ny = neighbors[i][1];
          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            const nIdx = ny * width + nx;
            if (visited[nIdx] === 0) {
              visited[nIdx] = 1;
              if (matches(nIdx * 4)) {
                queue.push(nx, ny);
              }
            }
          }
        }
      }
    }

    layer.ctx.putImageData(imgData, 0, 0);
  }
}
